import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { createOrganization } from '@/integrations/api/tenants_api';
import { useTenant } from '@/contexts/TenantContext';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateOrganizationDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const { reloadOrganizations, switchOrg } = useTenant();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      const org = await createOrganization({
        name: name.trim(),
        slug: slug.trim() || undefined,
      });
      await reloadOrganizations();
      await switchOrg(org.id);
      toast.success(t('iam.org.created', 'Organization created'));
      onOpenChange(false);
      setName('');
      setSlug('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error', 'Error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t('iam.org.createTitle', 'Create organization')}</DialogTitle>
            <DialogDescription>
              {t('iam.org.createDescription', 'Add a new plant or company workspace. You will be the owner.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="org-name">{t('iam.org.name', 'Name')}</Label>
              <Input
                id="org-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('iam.org.namePlaceholder', 'Acme Manufacturing')}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-slug">{t('iam.org.slug', 'Slug (optional)')}</Label>
              <Input
                id="org-slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="acme-mfg"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? t('common.loading', 'Loading…') : t('iam.org.create', 'Create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
